/**
 * Scenario Modeling ? Request Bond presentation helpers.
 * Pure functions only ? no network, no org-specific rules, no second workflow.
 */

/** Stable fingerprint of material model/project inputs used for a projection. */
export function fingerprintScenarioInputs(inputs, addedProjectEnabled, addedProject) {
  const base = {
    winRate: String(inputs?.winRate ?? ''),
    grossMargin: String(inputs?.grossMargin ?? ''),
    payTermDays: String(inputs?.payTermDays ?? ''),
    retainagePct: String(inputs?.retainagePct ?? ''),
    pipelineAward: String(inputs?.pipelineAward ?? ''),
    addedProjectEnabled: Boolean(addedProjectEnabled),
  };
  if (!addedProjectEnabled) {
    return JSON.stringify(base);
  }
  const p = addedProject || {};
  return JSON.stringify({
    ...base,
    projectName: String(p.projectName ?? ''),
    projectType: String(p.projectType ?? ''),
    contractValue: String(p.contractValue ?? ''),
    grossMarginEstimate: String(p.grossMarginEstimate ?? ''),
    startDate: String(p.startDate ?? ''),
    endDate: String(p.endDate ?? ''),
    paymentTermsDays: String(p.paymentTermsDays ?? ''),
    retainagePctProject: String(p.retainagePct ?? ''),
    apTimingDays: String(p.apTimingDays ?? ''),
    materialDepositAmount: String(p.materialDepositAmount ?? ''),
    bondingRequirement: String(p.bondingRequirement ?? ''),
    capitalRequirement: String(p.capitalRequirement ?? ''),
    confidencePct: String(p.confidencePct ?? ''),
  });
}

/**
 * Consistent currency presentation.
 * Prefer compact $2.26M / $25.0M for large magnitudes; full $2,256,994 when asked.
 */
export function formatScenarioCurrency(dollars, { style = 'compact' } = {}) {
  if (dollars === null || dollars === undefined || Number.isNaN(Number(dollars))) return '?';
  const n = Number(dollars);
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (style === 'full') {
    return `${sign}$${Math.round(abs).toLocaleString('en-US')}`;
  }
  if (abs >= 1_000_000) {
    const m = abs / 1_000_000;
    const digits = m >= 10 ? 1 : 2;
    return `${sign}$${m.toFixed(digits)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}$${Math.round(abs).toLocaleString('en-US')}`;
  }
  return `${sign}$${Math.round(abs).toLocaleString('en-US')}`;
}

/**
 * Format bare `$1234567` (no commas) to `$1,234,567` in customer-visible text.
 * Leaves already-comma'd or compact ($1.2M) amounts unchanged.
 */
export function formatBareDollarAmountsInText(text) {
  if (text == null || text === '') return text;
  return String(text).replace(/\$(\d{4,})(?!\d)/g, (_, digits) => {
    const n = Number(digits);
    if (!Number.isFinite(n)) return `$${digits}`;
    return `$${n.toLocaleString('en-US')}`;
  });
}

/** Undrawn LOC capacity: max(0, committed - drawn). Null drawn => null available (do not invent). */
export function availableLocCapacity(locFacilitySize, locDrawnBalance) {
  const loc = numOrNull(locFacilitySize);
  const drawn = numOrNull(locDrawnBalance);
  if (loc == null || drawn == null) return null;
  return Math.max(0, loc - drawn);
}

/**
 * ProjectionEngine semantics:
 *   availableLocHeadroom = undrawn LOC (credit_available, else committed - drawn)
 *   workingCapitalGap = max(0, -(projectedCashLow + availableLocHeadroom))
 *   where projectedCashLow is the lowest point of the displayed cash curve (before LOC draws).
 *
 * When the projection supplies availableLocHeadroom it is used as-is; otherwise it is derived
 * from committed and drawn LOC.
 *
 * Primary recommendation when gap > 0:
 *   additionalCapacityNeeded = gap
 *   modeledTargetLocFacility = committed LOC + gap
 *
 * Historical 110% of committed LOC is illustrative only:
 *   illustrativeFacilityAfterIncrease = committed ? 1.10
 *   illustrativeIncrementalIncrease = committed ? 0.10
 *   remainingUncoveredNeed = modeledTarget ? illustrativeFacility (when positive)
 */
export function explainCapitalRecommendation({
  locFacilitySize,
  locDrawnBalance,
  availableLocHeadroom,
  workingCapitalGap,
  recommendedAmount,
  illustrativeStepUpAmount,
  facilityType,
  rationale,
} = {}) {
  const loc = numOrNull(locFacilitySize);
  const drawn = numOrNull(locDrawnBalance);
  const headroom = numOrNull(availableLocHeadroom);
  const available = headroom != null
    ? Math.max(0, headroom)
    : availableLocCapacity(locFacilitySize, locDrawnBalance);
  const gap = numOrNull(workingCapitalGap);
  const amount = numOrNull(recommendedAmount);
  const illustrativeFromEngine = numOrNull(illustrativeStepUpAmount);
  const gapPositive = gap != null && gap > 0;
  const locPositive = loc != null && loc > 0;

  const additionalCapacityNeeded = gapPositive ? gap : 0;
  const modeledTargetLocFacility = gapPositive
    ? (loc != null ? loc + gap : gap)
    : loc;

  const expectedPrimary = gapPositive
    ? modeledTargetLocFacility
    : loc != null
      ? Math.max(loc, 0)
      : null;

  const matchesEngine =
    amount != null && expectedPrimary != null && Math.abs(amount - expectedPrimary) < 1;

  const illustrativeFacilityAfterIncrease =
    illustrativeFromEngine != null
      ? illustrativeFromEngine
      : locPositive
        ? loc * 1.1
        : null;
  const illustrativeIncrementalIncrease =
    locPositive && illustrativeFacilityAfterIncrease != null
      ? Math.max(0, illustrativeFacilityAfterIncrease - loc)
      : null;

  const targetValue = amount != null ? amount : modeledTargetLocFacility;
  const remainingUncoveredNeed =
    gapPositive && targetValue != null && illustrativeFacilityAfterIncrease != null
      ? Math.max(0, targetValue - illustrativeFacilityAfterIncrease)
      : 0;

  const shortfallBeforeLoc =
    gapPositive && available != null ? available + gap : gapPositive ? gap : null;

  const showIllustrative =
    illustrativeFacilityAfterIncrease != null
    && gapPositive
    && remainingUncoveredNeed > 0;

  // Primary reconciliation order (visually prioritized):
  // 1 committed, 2 available, 3 additional needed, 4 modeled target
  const primaryRows = [
    {
      key: 'currentLoc',
      label: 'Current committed LOC',
      value: loc,
      source: 'source_backed',
      note: 'Committed facility size from financial_snapshots.loc_facility_size.',
      priority: 'primary',
    },
    {
      key: 'availableLoc',
      label: 'Available LOC capacity',
      value: available,
      source: 'constructpay_projection',
      note: 'Undrawn LOC headroom (credit available, else committed - drawn).',
      priority: 'primary',
    },
    {
      key: 'additionalNeeded',
      label: 'Additional capacity needed',
      value: additionalCapacityNeeded,
      source: 'constructpay_projection',
      note: 'Equals the residual working-capital gap after undrawn LOC.',
      priority: 'primary',
    },
    {
      key: 'targetFacility',
      label: 'Modeled target LOC facility',
      value: targetValue,
      source: 'constructpay_projection',
      note: gapPositive
        ? 'Scenario-model facility size = committed LOC + residual gap. Not lender advice or an approved credit recommendation.'
        : 'Maintain current committed LOC under this scenario.',
      priority: 'primary',
    },
  ].filter((row) => row.value !== null && row.value !== undefined);

  // Secondary context
  const secondaryRows = [];
  if (drawn != null) {
    secondaryRows.push({
      key: 'drawnLoc',
      label: 'Current drawn / utilized',
      value: drawn,
      source: 'source_backed',
      note: 'Outstanding LOC balance from financial_snapshots.loc_balance_year_end.',
      priority: 'secondary',
    });
  }
  if (gap != null) {
    secondaryRows.push({
      key: 'modeledNeed',
      label: 'Modeled working-capital need (residual gap)',
      value: gap,
      source: 'constructpay_projection',
      note: 'max(0, -(lowest projected cash + available LOC headroom)).',
      priority: 'secondary',
    });
  }
  if (showIllustrative) {
    secondaryRows.push(
      {
        key: 'illustrativeFacility',
        label: 'Illustrative facility after 10% increase',
        value: illustrativeFacilityAfterIncrease,
        source: 'constructpay_projection',
        note: '110% of current committed LOC ? resulting facility size, not the incremental increase.',
        priority: 'secondary',
      },
      {
        key: 'illustrativeIncremental',
        label: 'Incremental increase (illustrative 10%)',
        value: illustrativeIncrementalIncrease,
        source: 'constructpay_projection',
        note: 'Committed LOC ? 10%.',
        priority: 'secondary',
      },
      {
        key: 'remainingUnfunded',
        label: 'Remaining uncovered need',
        value: remainingUncoveredNeed,
        source: 'constructpay_projection',
        note: 'Modeled target LOC facility ? illustrative facility after 10% increase.',
        priority: 'secondary',
      }
    );
  }

  return {
    quantityKind: gapPositive ? 'target_facility_covering_residual_gap' : 'maintain_current_facility',
    label: gapPositive ? 'Modeled target LOC facility' : 'Current facility (maintain)',
    explanation: gapPositive
      ? 'ConstructPay scenario model: residual working-capital gap after available undrawn LOC. Modeled target LOC facility = committed LOC + residual gap. Not lender advice.'
      : 'ConstructPay scenario model: no residual working-capital gap after available undrawn LOC ? maintain current committed facility.',
    facilityType: facilityType || 'Revolving LOC',
    currentLoc: loc,
    locDrawnBalance: drawn,
    availableLocCapacity: available,
    modeledWorkingCapitalGap: gap,
    modeledShortfallBeforeLoc: shortfallBeforeLoc,
    additionalCapacityNeeded,
    recommendedTargetFacility: targetValue,
    modeledTargetLocFacility: targetValue,
    illustrativeStepUpAmount: showIllustrative ? illustrativeFacilityAfterIncrease : null,
    illustrativeFacilityAfterIncrease: showIllustrative ? illustrativeFacilityAfterIncrease : null,
    illustrativeIncrementalIncrease: showIllustrative ? illustrativeIncrementalIncrease : null,
    remainingUncoveredNeed: remainingUncoveredNeed > 0 ? remainingUncoveredNeed : 0,
    remainingUnfundedNeed: remainingUncoveredNeed > 0 ? remainingUncoveredNeed : 0,
    engineOutputAmount: amount,
    matchesEngine,
    formulaDiscrepancy: false,
    formulaDiscrepancyNote: null,
    rationale: rationale || null,
    rows: [...primaryRows, ...secondaryRows],
    primaryRows,
    secondaryRows,
  };
}

export function buildRequestBondDecisionSummary({
  projectName,
  contractValue,
  bondingRequirement,
  workingCapitalImpact,
  capitalNeed,
  currentLoc,
  locDrawnBalance,
  capitalExplanation,
  riskState,
} = {}) {
  return {
    project: projectName || null,
    contractValue: numOrNull(contractValue),
    bondingDiscussionAmount: numOrNull(bondingRequirement),
    modeledWorkingCapitalImpact: numOrNull(workingCapitalImpact),
    modeledCapitalNeed: numOrNull(capitalNeed),
    currentLoc: numOrNull(currentLoc),
    locDrawnBalance: numOrNull(locDrawnBalance),
    availableLocCapacity: capitalExplanation?.availableLocCapacity ?? null,
    additionalCapacityNeeded: capitalExplanation?.additionalCapacityNeeded ?? null,
    recommendedTargetFacility: capitalExplanation?.modeledTargetLocFacility
      ?? capitalExplanation?.recommendedTargetFacility
      ?? null,
    illustrativeFacilityAfterIncrease: capitalExplanation?.illustrativeFacilityAfterIncrease ?? null,
    illustrativeIncrementalIncrease: capitalExplanation?.illustrativeIncrementalIncrease ?? null,
    remainingUncoveredNeed: capitalExplanation?.remainingUncoveredNeed
      ?? capitalExplanation?.remainingUnfundedNeed
      ?? null,
    riskState: riskState ? formatBareDollarAmountsInText(riskState) : null,
    reviewNotice: 'Review what will be shared before anything is sent.',
  };
}

/**
 * Why the modeled project cannot be carried into a Bond request yet, or null when it can.
 * A Bond request is always for one specific project, so Add a Project must be modeled.
 */
export function scenarioProjectContextIssue({ enabled, project } = {}) {
  if (!enabled) {
    return 'Add the project you want bonded under Add a Project, check "Model this project\'s impact", and recalculate before Request Bond.';
  }
  const missing = [];
  if (!String(project?.projectName ?? '').trim()) missing.push('project name');
  if (positiveOrNull(project?.contractValue) == null) missing.push('contract value');
  if (!isoDateOrNull(project?.startDate)) missing.push('start date');
  if (missing.length) {
    return `Add a Project needs a ${missing.join(', ')} before Request Bond.`;
  }
  const end = isoDateOrNull(project?.endDate);
  if (end && end < isoDateOrNull(project.startDate)) {
    return 'Add a Project end date must not be before the start date.';
  }
  if (!storableMoney(project.contractValue)) {
    return 'Contract value must be a whole-cent dollar amount.';
  }
  const bond = project?.bondingRequirement;
  if (bond !== undefined && bond !== null && String(bond).trim() !== '') {
    if (positiveOrNull(bond) == null) {
      return 'Estimated bonding requirement must be greater than zero, or left blank.';
    }
    if (!storableMoney(bond)) {
      return 'Estimated bonding requirement must be a whole-cent dollar amount.';
    }
  }
  return null;
}

// BondReady stores money as NUMERIC(19, 2); reject values it would round or overflow.
function storableMoney(v) {
  const n = Number(v);
  return Number.isFinite(n) && n < 1e17 && Math.abs(Math.round(n * 100) - n * 100) < 1e-6;
}

/**
 * Project context carried from Add a Project into the canonical BondReady Bond request.
 * Values are the customer's own Scenario Modeling entries; optional values stay null.
 */
export function buildScenarioProjectContext({ enabled, project, scenarioKey } = {}) {
  if (scenarioProjectContextIssue({ enabled, project })) return null;
  return {
    projectName: String(project.projectName).trim(),
    projectType: String(project.projectType ?? '').trim() || null,
    contractValue: positiveOrNull(project.contractValue),
    anticipatedStartDate: isoDateOrNull(project.startDate),
    anticipatedEndDate: isoDateOrNull(project.endDate),
    requestedBondAmount: positiveOrNull(project.bondingRequirement),
    scenarioKey: scenarioKey || null,
  };
}

/** True when a persisted Bond request carries exactly this Scenario Modeling project context. */
export function sameProjectContext(persisted, current) {
  if (!persisted || !current) return false;
  const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
  return String(persisted.projectName ?? '') === String(current.projectName ?? '')
    && (persisted.projectType ?? null) === (current.projectType ?? null)
    && num(persisted.contractValue) === num(current.contractValue)
    && num(persisted.requestedBondAmount) === num(current.requestedBondAmount)
    && (persisted.anticipatedStartDate ?? null) === (current.anticipatedStartDate ?? null)
    && (persisted.anticipatedEndDate ?? null) === (current.anticipatedEndDate ?? null);
}

/** Calendar date label for an ISO yyyy-mm-dd value without timezone shifting. */
export function formatProjectDate(iso) {
  const value = isoDateOrNull(iso);
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

function positiveOrNull(v) {
  const n = numOrNull(v);
  return n != null && n > 0 ? n : null;
}

function isoDateOrNull(v) {
  const s = String(v ?? '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

function numOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}
